/**
 * Todo 用 Zustand ストア
 *
 * フルアクセス権限を持つユーザーのみ使用可能
 */

import { create } from 'zustand'

import { CLIENT_ID } from '@/api/client'
import {
  createTodo as apiCreateTodo,
  deleteTodo as apiDeleteTodo,
  getTodo as apiGetTodo,
  listTodos,
  updateTodo as apiUpdateTodo,
} from '@/api/services/todo'
import { registerSyncListener } from '@/api/sync-events'
import type { TodoCreate, TodoResponse, TodoUpdate } from '@/api/types/todo'
import { clearNotionEditorBackup } from '@/lib/notion-editor-backup'

/**
 * 作成リクエストの多重送信ガード。
 * 電波が悪くレスポンスが返らないときにボタンを連打しても、
 * 同一内容のリクエストが飛行中なら同じ Promise を共有して重複登録を防ぐ。
 */
const pendingCreates = new Map<string, Promise<TodoResponse>>()

type TodoStoreState = {
  todos: TodoResponse[]
  isLoading: boolean
  error: string | null

  // タブ移動でも消えないドラフト (TodoInput 用)
  draftText: string
  setDraftText: (text: string) => void
  resetDraft: () => void

  loadTodos: (params?: { include_done?: boolean; completed_from?: string; completed_to?: string }) => Promise<void>
  addTodo: (payload: TodoCreate) => Promise<TodoResponse>
  duplicateTodo: (todo: TodoResponse) => Promise<TodoResponse>
  patchTodo: (id: number, payload: TodoUpdate) => Promise<TodoResponse>
  refreshTodo: (id: number) => Promise<TodoResponse | null>
  removeTodo: (id: number) => Promise<void>
}

export const useTodoStore = create<TodoStoreState>((set, get) => ({
  todos: [],
  isLoading: false,
  error: null,

  draftText: '',
  setDraftText: (text) => set({ draftText: text }),
  resetDraft: () => set({ draftText: '' }),

  loadTodos: async (params) => {
    set({ isLoading: true, error: null })
    try {
      const list = await listTodos({
        include_done: params?.include_done ?? false,
        completed_from: params?.completed_from,
        completed_to: params?.completed_to,
      })
      set({ todos: list, isLoading: false })
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'failed to load todos', isLoading: false })
    }
  },

  addTodo: async (payload) => {
    const key = JSON.stringify(payload)
    const inFlight = pendingCreates.get(key)
    if (inFlight) return inFlight

    const request = apiCreateTodo(payload)
      .then((created) => {
        set({ todos: [created, ...get().todos] })
        return created
      })
      .finally(() => {
        pendingCreates.delete(key)
      })
    pendingCreates.set(key, request)
    return request
  },

  duplicateTodo: async (todo) => {
    const payload: TodoCreate = {
      title: `${todo.title} (コピー)`,
      deadline: todo.deadline,
      estimated_minutes: todo.estimated_minutes,
      priority: todo.priority,
      urgency: todo.urgency,
      energy: todo.energy,
      status: 'pending',
      source: 'manual',
      parent_id: todo.parent_id,
      tags: [...todo.tags],
    }
    const created = await apiCreateTodo(payload)
    // 子 Todo も複製（並列）
    const children = get().todos.filter((t) => t.parent_id === todo.id)
    const createdChildren = await Promise.all(
      children.map((child) =>
        apiCreateTodo({
          title: child.title,
          estimated_minutes: child.estimated_minutes,
          priority: child.priority,
          urgency: child.urgency,
          energy: child.energy,
          status: 'pending',
          source: 'manual',
          parent_id: created.id,
        }),
      ),
    )
    set({ todos: [...createdChildren, created, ...get().todos] })
    return created
  },

  patchTodo: async (id, payload) => {
    const updated = await apiUpdateTodo(id, payload)
    set({ todos: get().todos.map((t) => (t.id === id ? updated : t)) })
    return updated
  },

  refreshTodo: async (id) => {
    try {
      const fresh = await apiGetTodo(id)
      set({ todos: get().todos.map((t) => (t.id === id ? fresh : t)) })
      return fresh
    } catch (err) {
      console.warn('failed to refresh todo', err)
      return null
    }
  },

  removeTodo: async (id) => {
    // CASCADE で消える子 Todo の ID を先に確定させる (削除後は state から消えてしまうため)
    const childIds = get()
      .todos.filter((t) => t.parent_id === id)
      .map((t) => t.id)
    await apiDeleteTodo(id)
    // localStorage 上のメモバックアップも削除。
    // 同一 ID が再採番された TODO に旧本文が復活するのを防ぐ。
    clearNotionEditorBackup('todo', id)
    for (const childId of childIds) {
      clearNotionEditorBackup('todo', childId)
    }
    // CASCADE: 子 Todo もストアから除去
    set({
      todos: get().todos.filter((t) => t.id !== id && t.parent_id !== id),
    })
  },
}))

// -----------------------------------------------------------------------------
// SSE によるマルチ端末同期: 他端末で行われた変更を Store に反映する
// -----------------------------------------------------------------------------
registerSyncListener((event) => {
  // 自端末発のイベントは楽観的更新済のため無視
  if ('origin' in event && event.origin && event.origin === CLIENT_ID) return

  const store = useTodoStore.getState()

  switch (event.type) {
    case 'todo.created':
    case 'todo.updated':
      // API から最新状態を取得。無ければ prepend する
      void apiGetTodo(event.id)
        .then((fresh) => {
          const todos = useTodoStore.getState().todos
          const idx = todos.findIndex((t) => t.id === fresh.id)
          if (idx === -1) {
            useTodoStore.setState({ todos: [fresh, ...todos] })
          } else {
            useTodoStore.setState({
              todos: todos.map((t) => (t.id === fresh.id ? fresh : t)),
            })
          }
        })
        .catch((err) => console.warn('[sync] refresh todo failed', err))
      break
    case 'todo.deleted': {
      useTodoStore.setState({
        todos: store.todos.filter((t) => t.id !== event.id && t.parent_id !== event.id),
      })
      break
    }
    default:
      break
  }
})
